import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { DuplicateAccountsPanel } from '../components/DuplicateAccountsPanel';
import AdminAuditDialog from '../components/AdminAuditDialog';
import { PageHeader } from '../components/AppLayout';
import { Avatar } from '../components/Logo';
import { Check, X as XIcon, ArrowLeft, Calendar as CalIcon, Eye, Trash2, Loader2, RotateCcw } from '../components/icons';
import { localToday, addDaysISO, parseLocalDate, formatDay } from '../lib/dates';

const PERIODS = [
  { id: 'today',  label: 'Aujourd\'hui' },
  { id: 'week',   label: 'Cette semaine' },
  { id: 'month',  label: '30 derniers jours' },
  { id: 'custom', label: 'Personnalisé' },
];

// today ISO helper — Iter61l: local timezone
const todayISO = () => localToday();
const addDays = (iso, n) => addDaysISO(iso, n);

export default function TeamPage() {
  const { isFounder, isFounderStrict, impersonate } = useAuth();
  const [period, setPeriod] = useState('today');
  const [customStart, setCustomStart] = useState(addDays(todayISO(), -6));
  const [customEnd, setCustomEnd] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeUserId, setActiveUserId] = useState(null);
  // Iter47n — Founder-only pending signups + dynamic user list
  const [pending, setPending] = useState([]);
  const [dynUsers, setDynUsers] = useState([]);
  const [revealed, setRevealed] = useState({}); // {userId: 'plaintext'}
  // Iter 62fh — Comptes invités (magic-link temp access)
  const [guests, setGuests] = useState([]);
  const [showGuestCreator, setShowGuestCreator] = useState(false);
  const [newGuestInvite, setNewGuestInvite] = useState(null); // {name, invite_url, expires_at}

  const loadGuests = () => {
    if (!isFounder) return;
    api.get('/guest-accounts').then((r) => setGuests(r.data || [])).catch(() => setGuests([]));
  };

  const load = () => {
    setLoading(true);
    const url = period === 'custom'
      ? `/team/productivity?period=custom&start=${customStart}&end=${customEnd}`
      : `/team/productivity?period=${period}`;
    api.get(url)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  const loadPending = () => {
    if (!isFounder) return;
    api.get('/users/pending').then((r) => setPending(r.data)).catch(() => {});
    api.get('/users/dynamic').then((r) => setDynUsers(r.data)).catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, customStart, customEnd]);
  useEffect(() => { loadPending(); loadGuests(); /* eslint-disable-next-line */ }, [isFounder]);

  const approvePending = async (uid) => {
    try { await api.post(`/users/${uid}/approve`); toast.success('Compte approuvé'); loadPending(); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const rejectPending = async (uid) => {
    try { await api.post(`/users/${uid}/reject`); toast.success('Compte refusé'); loadPending(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const deleteDynUser = async (uid) => {
    if (!window.confirm('Archiver ce compte ?\n(Son historique de tâches sera préservé — tu pourras le restaurer.)')) return;
    try { await api.delete(`/users/${uid}`); toast.success('Membre supprimé'); loadPending(); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const restoreDynUser = async (uid) => {
    try { await api.post(`/users/${uid}/restore`); toast.success('Compte restauré'); loadPending(); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const revealPwd = async (uid) => {
    if (revealed[uid]) { setRevealed({ ...revealed, [uid]: null }); return; }
    try {
      const r = await api.get(`/users/${uid}/reveal-password`);
      setRevealed({ ...revealed, [uid]: r.data.password });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Impossible de récupérer le mot de passe'); }
  };

  // Iter 62cn — Founder impersonation
  const goSeeAsUser = async (uid, name) => {
    if (!window.confirm(`Voir ASA depuis le compte de ${name} ? Un bandeau en haut de l'écran te permettra de revenir à ton compte à tout moment.`)) return;
    try {
      await impersonate(uid);
      window.location.href = '/';
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de basculer sur ce compte');
    }
  };

  // Iter60c — rename user
  const [renamingUser, setRenamingUser] = useState(null); // {id, first_name, last_name}
  const [renameFirst, setRenameFirst] = useState('');
  const [renameLast, setRenameLast] = useState('');
  const openRename = (u) => {
    setRenamingUser(u);
    setRenameFirst(u.first_name || '');
    setRenameLast(u.last_name || '');
  };
  const submitRename = async () => {
    if (!renamingUser) return;
    if (!renameFirst.trim() && !renameLast.trim()) { toast.error('Rien à renommer'); return; }
    try {
      await api.patch(`/users/${renamingUser.id}/rename`, {
        first_name: renameFirst.trim() || undefined,
        last_name: renameLast.trim() || undefined,
      });
      toast.success('Membre renommé');
      setRenamingUser(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const users = data?.users || [];
  const contributors = useMemo(() => {
    // Iter 62ct — Include gestionnaire flagged is_monteur (Aryan) in Team monteur listing
    const base = users.filter((u) => u.role === 'contributor' || u.is_monteur === true);
    // Iter47o — merge archived dynamic users (excluded from productivity endpoint by design)
    // so they still show up dimmed with a Restaurer button
    const archivedDyn = dynUsers
      .filter((d) => d.is_archived && d.role === 'contributor' && !base.some((u) => u.id === d.id))
      .map((d) => ({ ...d, assigned: 0, done: 0, pct: 0, late: 0 }));
    return [...base, ...archivedDyn];
  }, [users, dynUsers]);
  const activeUser = users.find((u) => u.id === activeUserId);

  return (
    <div className="page-fade">
      <PageHeader title="Équipe & Progression" subtitle="Suivi de la productivité — vert = tout fait · rouge = tâches loupées" />

      <div style={{ padding: '0 22px 100px', maxWidth: 1100, margin: '0 auto' }}>
        {isFounderStrict && <DuplicateAccountsPanel onMerged={() => { load(); loadPending(); }} />}
        {/* Period tabs */}
        <div style={{
          display: 'inline-flex', gap: 2, padding: 4, borderRadius: 12,
          background: 'var(--sf)', border: '1px solid var(--bd)', marginBottom: 14,
        }}>
          {PERIODS.map((p) => (
            <button
              key={p.id}
              data-testid={`team-period-${p.id}`}
              onClick={() => setPeriod(p.id)}
              style={{
                padding: '9px 16px', borderRadius: 9, border: 'none',
                background: period === p.id ? 'rgba(46,163,242,0.14)' : 'transparent',
                color: period === p.id ? 'var(--p)' : 'var(--t2)',
                fontFamily: 'Sora', fontWeight: 700, fontSize: 12, cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >{p.label}</button>
          ))}
        </div>

        {period === 'custom' && (
          <div style={{
            display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14,
            padding: '10px 14px', borderRadius: 12, background: 'var(--sf)', border: '1px solid var(--bd)',
          }}>
            <CalIcon size={14} color="var(--t3)" />
            <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: 'var(--t3)' }}>Du</span>
            <input data-testid="custom-start" type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
              style={{ background: 'var(--sf2)', border: '1px solid var(--bd)', borderRadius: 8, padding: '5px 9px', color: 'var(--t1)', fontFamily: 'Sora', fontSize: 12 }} />
            <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: 'var(--t3)' }}>au</span>
            <input data-testid="custom-end" type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
              style={{ background: 'var(--sf2)', border: '1px solid var(--bd)', borderRadius: 8, padding: '5px 9px', color: 'var(--t1)', fontFamily: 'Sora', fontSize: 12 }} />
          </div>
        )}

        {loading ? (
          <div className="shimmer" style={{ height: 220, borderRadius: 14 }} />
        ) : activeUser ? (
          period === 'today' ? (
            <UserTaskListView
              user={activeUser}
              onBack={() => setActiveUserId(null)}
            />
          ) : (
            <UserCalendarView
              user={activeUser}
              startISO={data?.start}
              endISO={data?.end}
              onBack={() => setActiveUserId(null)}
            />
          )
        ) : (
          <>
            {/* Iter 62fh — Comptes invités (founder only) */}
            {isFounder && (
              <GuestsSection
                guests={guests}
                onUpdated={loadGuests}
                onCreate={(role) => setShowGuestCreator(role || 'autre')}
                onRevoke={async (gid) => {
                  if (!window.confirm('Révoquer ce compte invité ? Son lien ne fonctionnera plus.')) return;
                  try { await api.delete(`/guest-accounts/${gid}`); toast.success('Invité révoqué'); loadGuests(); }
                  catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
                }}
              />
            )}
            {/* Iter47n — Comptes en attente (founder only) */}
            {isFounder && pending.length > 0 && (
              <div
                data-testid="pending-users-section"
                style={{ marginBottom: 18 }}
              >
                <div className="asa-eyebrow" style={{ marginBottom: 8, color: '#F59E0B' }}>
                  Comptes en attente · {pending.length}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {pending.map((p) => (
                    <div
                      key={p.id}
                      data-testid={`pending-user-${p.id}`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 14px', borderRadius: 11,
                        background: 'rgba(245,158,11,0.06)',
                        border: '1.5px solid rgba(245,158,11,0.30)',
                      }}
                    >
                      <Avatar user={{ name: `${p.first_name} ${p.last_name}`, color: '#F59E0B', short: (p.first_name[0] + p.last_name[0]).toUpperCase() }} size={30} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t1)' }}>
                          {p.first_name} {p.last_name}
                        </div>
                        <div style={{ fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)' }}>{p.email}</div>
                      </div>
                      <button
                        data-testid={`approve-user-${p.id}`}
                        onClick={() => approvePending(p.id)}
                        className="asa-btn"
                        style={{ height: 30, padding: '0 12px', fontSize: 11.5, background: '#34D399', border: 'none' }}
                      >
                        <Check size={12} /> Approuver
                      </button>
                      <button
                        data-testid={`reject-user-${p.id}`}
                        onClick={() => rejectPending(p.id)}
                        className="asa-btn asa-btn-ghost"
                        style={{ height: 30, padding: '0 10px', fontSize: 11.5, color: '#EF4444', borderColor: '#EF444455' }}
                      >
                        <XIcon size={12} /> Refuser
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {contributors.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--t3)', fontFamily: 'Lexend', fontSize: 12.5, background: 'var(--sf)', borderRadius: 12, border: '1px dashed var(--bd)' }}>
                  Aucun monteur.
                </div>
              ) : (
                contributors.map((u) => {
                  const isDynamic = dynUsers.some((d) => d.id === u.id);
                  const canDelete = isDynamic || (u.role !== 'founder' && !String(u.id).startsWith('guest_'));
                  const isArchived = !!u.is_archived;
                  return (
                    <UserLine
                      key={u.id}
                      user={u}
                      dimmed={isArchived}
                      onClick={isArchived ? null : () => setActiveUserId(u.id)}
                      extra={isFounder ? (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                          {!isArchived && isFounderStrict && (
                            <button
                              data-testid={`impersonate-${u.id}`}
                              onClick={() => goSeeAsUser(u.id, u.name || u.first_name || 'ce compte')}
                              title="Voir la vue de ce monteur"
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                height: 26, padding: '0 10px', borderRadius: 6,
                                background: 'linear-gradient(135deg, rgba(91,190,252,0.16) 0%, rgba(30,95,255,0.20) 100%)',
                                border: '1px solid rgba(91,190,252,0.45)',
                                color: '#5BBEFC',
                                fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
                                letterSpacing: '0.02em', cursor: 'pointer',
                              }}
                            ><Eye size={12} /> Voir sa vue</button>
                          )}
                          {!isArchived && (
                            <button
                              data-testid={`rename-user-${u.id}`}
                              onClick={() => openRename(u)}
                              title="Renommer"
                              style={{
                                width: 26, height: 26, borderRadius: 6,
                                background: 'var(--sf2)', border: '1px solid var(--bd)',
                                display: 'grid', placeItems: 'center', cursor: 'pointer',
                                color: 'var(--t3)',
                              }}
                            >{/* pencil */}
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                            </button>
                          )}
                          {isDynamic && isArchived && (
                            <span
                              data-testid={`archived-badge-${u.id}`}
                              style={{
                                fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5,
                                padding: '3px 8px', borderRadius: 999,
                                background: 'rgba(148,163,184,0.14)',
                                color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em',
                              }}
                            >Archivé</span>
                          )}
                          {isDynamic && !isArchived && revealed[u.id] && (
                            <code
                              data-testid={`revealed-password-${u.id}`}
                              style={{
                                fontFamily: 'monospace', fontSize: 11,
                                padding: '3px 8px', borderRadius: 6,
                                background: 'rgba(91,190,252,0.12)',
                                color: '#5BBEFC',
                              }}
                            >{revealed[u.id]}</code>
                          )}
                          {isDynamic && !isArchived && (
                            <button
                              data-testid={`reveal-pwd-${u.id}`}
                              onClick={() => revealPwd(u.id)}
                              title="Voir le mot de passe"
                              style={{
                                width: 26, height: 26, borderRadius: 6,
                                background: 'var(--sf2)', border: '1px solid var(--bd)',
                                display: 'grid', placeItems: 'center', cursor: 'pointer',
                                color: 'var(--t3)',
                              }}
                            ><Eye size={12} /></button>
                          )}
                          {canDelete && (isArchived ? (
                            <button
                              data-testid={`restore-user-${u.id}`}
                              onClick={() => restoreDynUser(u.id)}
                              title="Restaurer ce compte"
                              style={{
                                display: 'flex', alignItems: 'center', gap: 5,
                                height: 26, padding: '0 10px', borderRadius: 6,
                                background: 'rgba(52,211,153,0.14)', border: '1px solid rgba(52,211,153,0.35)',
                                cursor: 'pointer', color: '#34D399',
                                fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
                              }}
                            ><RotateCcw size={11} /> Restaurer</button>
                          ) : (
                            <button
                              data-testid={`delete-user-${u.id}`}
                              onClick={() => deleteDynUser(u.id)}
                              title="Archiver le compte (soft delete)"
                              style={{
                                width: 26, height: 26, borderRadius: 6,
                                background: 'var(--sf2)', border: '1px solid var(--bd)',
                                display: 'grid', placeItems: 'center', cursor: 'pointer',
                                color: '#EF4444',
                              }}
                            ><Trash2 size={12} /></button>
                          ))}
                        </div>
                      ) : null}
                    />
                  );
                })
              )}
            </div>
          </>
        )}
      </div>

      {/* Iter60c — Rename user modal */}
      {renamingUser && (
        <div
          onClick={() => setRenamingUser(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)',
            zIndex: 500, display: 'grid', placeItems: 'center', padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--sf)', border: '1px solid var(--bd)',
              borderRadius: 16, padding: 22, width: '100%', maxWidth: 380,
              boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 15, color: 'var(--t1)', marginBottom: 4 }}>
              Renommer un membre
            </div>
            <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)', marginBottom: 16 }}>
              {renamingUser.first_name} {renamingUser.last_name}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <label className="asa-eyebrow" style={{ fontSize: '0.55rem', marginBottom: 5, display: 'block' }}>Prénom</label>
                <input
                  data-testid="rename-first-name"
                  value={renameFirst}
                  onChange={(e) => setRenameFirst(e.target.value)}
                  autoFocus
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8,
                    background: 'var(--bg)', border: '1px solid var(--bd)',
                    color: 'var(--t1)', fontFamily: 'Sora', fontWeight: 600, fontSize: 13,
                  }}
                />
              </div>
              <div>
                <label className="asa-eyebrow" style={{ fontSize: '0.55rem', marginBottom: 5, display: 'block' }}>Nom</label>
                <input
                  data-testid="rename-last-name"
                  value={renameLast}
                  onChange={(e) => setRenameLast(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8,
                    background: 'var(--bg)', border: '1px solid var(--bd)',
                    color: 'var(--t1)', fontFamily: 'Sora', fontWeight: 600, fontSize: 13,
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setRenamingUser(null)}
                style={{
                  padding: '9px 14px', borderRadius: 8,
                  background: 'transparent', border: '1px solid var(--bd)',
                  color: 'var(--t2)', fontFamily: 'Sora', fontWeight: 700, fontSize: 12, cursor: 'pointer',
                }}
              >Annuler</button>
              <button
                data-testid="rename-save"
                onClick={submitRename}
                style={{
                  padding: '9px 18px', borderRadius: 8,
                  background: '#2EA3F2', border: 'none', color: '#fff',
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 12, cursor: 'pointer',
                }}
              >Enregistrer</button>
            </div>
          </div>
        </div>
      )}
      {/* Iter 62fh — Dialog de création compte invité */}
      {showGuestCreator && (
        <GuestCreatorDialog
          onClose={() => { setShowGuestCreator(false); setNewGuestInvite(null); }}
          onCreated={(invite) => { setNewGuestInvite(invite); loadGuests(); }}
          created={newGuestInvite}
          initialRole={typeof showGuestCreator === 'string' ? showGuestCreator : 'autre'}
        />
      )}
    </div>
  );
}

// Simple 1-line row per user
function UserLine({ user, onClick, extra, dimmed = false }) {
  const pctColor = user.pct >= 80 ? '#34D399' : user.pct >= 50 ? '#F5A623' : '#EF4444';
  const hasTasks = user.assigned > 0;
  const clickable = !!onClick;
  return (
    <div
      data-testid={`team-user-${user.id}`}
      role="button"
      tabIndex={clickable ? 0 : -1}
      aria-disabled={!clickable}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '11px 14px', borderRadius: 11,
        background: 'var(--sf)', border: '1px solid var(--bd)',
        cursor: clickable ? 'pointer' : 'default', textAlign: 'left', transition: 'all 0.12s',
        opacity: dimmed ? 0.55 : 1,
        filter: dimmed ? 'grayscale(0.6)' : 'none',
        userSelect: 'none',
      }}
      onMouseEnter={(e) => { if (!clickable) return; e.currentTarget.style.borderColor = 'rgba(46,163,242,0.4)'; e.currentTarget.style.background = 'rgba(46,163,242,0.04)'; }}
      onMouseLeave={(e) => { if (!clickable) return; e.currentTarget.style.borderColor = 'var(--bd)'; e.currentTarget.style.background = 'var(--sf)'; }}
    >
      <Avatar user={user} size={30} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t1)' }}>{user.name}</span>
        {user.job_title && (
          <span
            data-testid={`team-user-job-${user.id}`}
            style={{
              fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5, color: 'var(--t3)',
              letterSpacing: '0.03em', textTransform: 'uppercase', marginTop: 1,
            }}
          >
            {user.job_title}
          </span>
        )}
      </div>
      {extra}
      {hasTasks ? (
        <>
          <span style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: 'var(--t3)' }}>
            {user.done} / {user.assigned}
          </span>
          <span style={{
            padding: '2px 10px', borderRadius: 999,
            background: `${pctColor}22`, color: pctColor,
            fontFamily: 'Sora', fontWeight: 800, fontSize: 11.5,
            minWidth: 48, textAlign: 'center',
          }}>{user.pct}%</span>
        </>
      ) : (
        <span style={{ fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)', fontStyle: 'italic' }}>
          Aucune tâche
        </span>
      )}
    </div>
  );
}

// Calendar drill-down: shows all days in the period; green if all done, red if any missed
function UserCalendarView({ user, startISO, endISO, onBack }) {
  const [pickedDay, setPickedDay] = useState(null);

  // Build daily aggregates from user.tasks
  const daily = useMemo(() => {
    const map = new Map();
    (user.tasks || []).forEach((t) => {
      const d = (t.due_date || '').slice(0, 10);
      if (!d) return;
      if (!map.has(d)) map.set(d, { total: 0, done: 0, tasks: [] });
      const bucket = map.get(d);
      bucket.total += 1;
      if (t.stage === 'done') bucket.done += 1;
      bucket.tasks.push(t);
    });
    return map;
  }, [user.tasks]);

  // Build the day range
  const days = [];
  if (startISO && endISO) {
    let cursor = startISO;
    let safety = 0;
    while (cursor <= endISO && safety < 400) {
      days.push(cursor);
      cursor = addDays(cursor, 1);
      safety += 1;
    }
  }

  const dayData = pickedDay ? daily.get(pickedDay) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-testid={`user-calendar-${user.id}`}>
      <button
        onClick={onBack}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '7px 12px', borderRadius: 9,
          background: 'var(--sf)', border: '1px solid var(--bd)',
          color: 'var(--t2)', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
          cursor: 'pointer', width: 'fit-content',
        }}
      >
        <ArrowLeft size={12} /> Retour à l&apos;équipe
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Avatar user={user} size={40} />
        <div>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 16, color: 'var(--t1)', letterSpacing: '-0.01em' }}>{user.name}</div>
          <div style={{ fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)', marginTop: 1 }}>
            {user.done} tâches terminées sur {user.assigned} · {user.pct}% de productivité
          </div>
        </div>
      </div>

      {/* Calendar grid */}
      <div style={{ padding: 14, borderRadius: 14, background: 'var(--sf)', border: '1px solid var(--bd)' }}>
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
          Calendrier · vert = tout fait · rouge = tâches loupées
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 8 }}>
          {days.map((d) => {
            const bucket = daily.get(d);
            const label = formatDay(d, { day: '2-digit', month: 'short' });
            let color = '#3A4A66';
            let bg = 'var(--sf2)';
            let border = 'var(--bd)';
            if (bucket) {
              const allDone = bucket.done === bucket.total;
              color = allDone ? '#34D399' : '#EF4444';
              bg = allDone ? 'rgba(52,211,153,0.14)' : 'rgba(239,68,68,0.14)';
              border = allDone ? '#34D39966' : '#EF444466';
            }
            const isPicked = pickedDay === d;
            return (
              <button
                key={d}
                data-testid={`calendar-day-${d}`}
                onClick={() => bucket && setPickedDay(pickedDay === d ? null : d)}
                disabled={!bucket}
                style={{
                  padding: '10px 8px', borderRadius: 10,
                  background: bg, border: `1.5px solid ${isPicked ? color : border}`,
                  color: bucket ? color : 'var(--t3)',
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
                  cursor: bucket ? 'pointer' : 'default',
                  transition: 'all 0.15s',
                  transform: isPicked ? 'scale(1.05)' : 'scale(1)',
                  opacity: bucket ? 1 : 0.35,
                }}
              >
                {label}
                {bucket && (
                  <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 10, marginTop: 2 }}>
                    {bucket.done}/{bucket.total}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day tasks */}
      {dayData && (
        <div style={{ padding: 14, borderRadius: 14, background: 'var(--sf)', border: '1px solid var(--bd)' }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 12, color: 'var(--t1)', marginBottom: 10 }}>
            {formatDay(pickedDay, { weekday: 'long', day: 'numeric', month: 'long' })}
            <span style={{ color: 'var(--t3)', fontWeight: 400, marginLeft: 8 }}>· {dayData.done}/{dayData.total} tâches faites</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {dayData.tasks.map((t) => (
              <div key={t.id} data-testid={`day-task-${t.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '7px 10px', borderRadius: 8,
                  background: 'var(--sf2)', border: `1px solid ${t.stage === 'done' ? '#34D39944' : '#EF444444'}`,
                }}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%',
                  background: t.stage === 'done' ? 'rgba(52,211,153,0.22)' : 'rgba(239,68,68,0.22)',
                  border: `1.5px solid ${t.stage === 'done' ? '#34D399' : '#EF4444'}`,
                  display: 'grid', placeItems: 'center', flexShrink: 0,
                }}>
                  {t.stage === 'done'
                    ? <Check size={10} color="#34D399" strokeWidth={2.8} />
                    : <XIcon size={9} color="#EF4444" strokeWidth={2.8} />}
                </div>
                <span style={{
                  flex: 1, fontFamily: 'Sora', fontWeight: 600, fontSize: 12,
                  color: t.stage === 'done' ? 'var(--t3)' : 'var(--t1)',
                  textDecoration: t.stage === 'done' ? 'line-through' : 'none',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{t.title}</span>
                {t.kind === 'video' && (
                  <span style={{
                    padding: '1px 6px', borderRadius: 999,
                    background: 'rgba(239,68,68,0.16)', color: '#EF4444',
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>Vidéo</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// Iter47j — Simple task list view for the "Aujourd'hui" period
// User clicks a monteur → sees ALL his tasks for today (no calendar).
function UserTaskListView({ user, onBack }) {
  const tasks = (user.tasks || []).slice().sort((a, b) => {
    // Done last, then by due
    if ((a.stage === 'done') !== (b.stage === 'done')) return a.stage === 'done' ? 1 : -1;
    return (a.due_date || '').localeCompare(b.due_date || '');
  });
  const doneCount = tasks.filter((t) => t.stage === 'done').length;

  return (
    <div className="page-fade" data-testid={`team-user-tasklist-${user.id}`}>
      <button
        onClick={onBack}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '7px 12px', borderRadius: 9,
          background: 'var(--sf)', border: '1px solid var(--bd)', color: 'var(--t2)',
          fontFamily: 'Sora', fontWeight: 700, fontSize: 12, cursor: 'pointer', marginBottom: 14,
        }}
      >
        <ArrowLeft size={12} /> Retour
      </button>

      <div style={{
        padding: '14px 16px', borderRadius: 14,
        background: 'var(--sf)', border: '1px solid var(--bd)', marginBottom: 12,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 15, color: 'var(--t1)' }}>
            Tâches d&apos;aujourd&apos;hui · {user.name}
          </div>
          {user.job_title && (
            <div style={{ fontFamily: 'Sora', fontWeight: 600, fontSize: 10, color: 'var(--t3)', letterSpacing: '0.05em', textTransform: 'uppercase', marginTop: 2 }}>
              {user.job_title}
            </div>
          )}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: 12, color: 'var(--t3)',
          }}>{doneCount} / {tasks.length} faites</span>
          <span style={{
            padding: '4px 12px', borderRadius: 999,
            background: `${user.pct >= 80 ? '#34D399' : user.pct >= 50 ? '#F5A623' : '#EF4444'}22`,
            color: user.pct >= 80 ? '#34D399' : user.pct >= 50 ? '#F5A623' : '#EF4444',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5,
          }}>{user.pct}%</span>
        </div>
      </div>

      {tasks.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--t3)', fontFamily: 'Lexend', fontSize: 12.5, background: 'var(--sf)', borderRadius: 14, border: '1px dashed var(--bd)' }}>
          Aucune tâche assignée aujourd&apos;hui.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {tasks.map((t) => {
            const done = t.stage === 'done';
            const isVideo = t.kind === 'video';
            return (
              <div
                key={t.id}
                data-testid={`team-task-${t.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', borderRadius: 10,
                  background: 'var(--sf)', border: '1px solid var(--bd)',
                  borderLeft: isVideo && !done ? '3px solid #EF4444' : `1px solid var(--bd)`,
                }}
              >
                <div style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: done ? 'rgba(52,211,153,0.22)' : 'rgba(46,163,242,0.10)',
                  border: `1.5px solid ${done ? '#34D399' : 'var(--bd)'}`,
                  display: 'grid', placeItems: 'center', flexShrink: 0,
                }}>
                  {done && <Check size={12} color="#34D399" strokeWidth={2.6} />}
                </div>
                <span style={{
                  flex: 1, fontFamily: 'Sora', fontWeight: 600, fontSize: 13,
                  color: done ? 'var(--t3)' : 'var(--t1)',
                  textDecoration: done ? 'line-through' : 'none',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{t.title}</span>
                {isVideo && !done && (
                  <span style={{
                    padding: '2px 8px', borderRadius: 999,
                    background: 'rgba(239,68,68,0.16)', color: '#EF4444',
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 9,
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                  }}>Vidéo</span>
                )}
                {t.due_date && (
                  <span style={{
                    fontFamily: 'Sora', fontWeight: 600, fontSize: 11, color: 'var(--t3)',
                  }}>
                    {formatDay(t.due_date, { day: '2-digit', month: 'short' })}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Iter 62fh — Comptes Invités (Guest Accounts) ───────────────────────────
function fmtExpire(iso) {
  if (!iso) return 'Pas d\'expiration';
  try {
    const d = new Date(iso);
    const diff = d.getTime() - Date.now();
    if (diff < 0) return 'Expiré';
    const h = Math.floor(diff / 3600000);
    if (h < 1) return 'Expire dans <1h';
    if (h < 24) return `Expire dans ${h}h`;
    const days = Math.floor(h / 24);
    if (days < 30) return `Expire dans ${days}j`;
    return `Expire le ${d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}`;
  } catch { return iso; }
}
const GUEST_ROLE_OPTS = [
  { id: 'posteur', label: 'Posteur' },
  { id: 'monteur', label: 'Monteur' },
  { id: 'derusheur', label: 'Dérusheur' },
  { id: 'autre', label: 'Autre' },
];

async function patchGuest(gid, body, okMsg, onUpdated) {
  try {
    await api.patch(`/guest-accounts/${gid}`, body);
    toast.success(okMsg);
    onUpdated?.();
  } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
}

function GuestRowActions({ g, onUpdated }) {
  const small = { height: 30, padding: '0 10px', fontSize: 11.5 };
  return (
    <>
      <select
        data-testid={`guest-role-${g.id}`}
        value={g.job_role || 'autre'}
        onChange={(e) => patchGuest(g.id, { job_role: e.target.value }, 'Rôle mis à jour', onUpdated)}
        style={{ height: 30, padding: '0 8px', borderRadius: 8, background: 'var(--sf)', border: '1px solid var(--bd)', color: 'var(--t1)', fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5 }}
      >
        {GUEST_ROLE_OPTS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
      </select>
      <button
        data-testid={`rename-guest-${g.id}`}
        onClick={() => {
          const n = window.prompt('Nouveau nom de l\'invité', g.name || '');
          if (n && n.trim()) patchGuest(g.id, { name: n.trim() }, 'Invité renommé', onUpdated);
        }}
        className="asa-btn asa-btn-ghost"
        style={small}
      >Renommer</button>
      <button
        data-testid={`extend-guest-${g.id}`}
        onClick={() => patchGuest(g.id, { extend_hours: 168 }, 'Accès prolongé de 7 jours', onUpdated)}
        className="asa-btn asa-btn-ghost"
        style={{ ...small, color: '#34D399', borderColor: '#34D39955' }}
      >+7 j</button>
      <button
        data-testid={`extend30-guest-${g.id}`}
        onClick={() => patchGuest(g.id, { extend_hours: 720 }, 'Accès prolongé de 30 jours', onUpdated)}
        className="asa-btn asa-btn-ghost"
        style={{ ...small, color: '#34D399', borderColor: '#34D39955' }}
      >+30 j</button>
    </>
  );
}

function GuestsSection({ guests, onCreate, onRevoke, onUpdated }) {
  const active = guests.filter((g) => g.status === 'active' || g.status === 'expired');
  return (
    <div data-testid="guests-section" style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div className="asa-eyebrow" style={{ color: '#F5C56A', margin: 0 }}>
          Comptes invités · {active.length}
        </div>
        <div style={{ flex: 1 }} />
        <button
          data-testid="create-poster-invite-btn"
          onClick={() => onCreate('posteur')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 10,
            background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)',
            border: 'none', color: '#0B1330',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 12, cursor: 'pointer',
          }}
        >📱 Lien posteur</button>
        <button
          data-testid="create-guest-btn"
          onClick={() => onCreate('autre')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 10,
            background: 'linear-gradient(180deg, #F5C56A 0%, #D99A42 100%)',
            border: 'none', color: '#0B1330',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 12, cursor: 'pointer',
            boxShadow: '0 8px 20px -6px rgba(245,197,106,0.55), inset 0 1px 0 rgba(255,255,255,0.28)',
          }}
        >+ Créer un compte invité</button>
      </div>
      <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 11.5, color: 'var(--t3)', marginBottom: 10, lineHeight: 1.5 }}>
        Génère un lien à partager (ex. monteur freelance). L'invité verra uniquement l'onglet Tâches avec les tâches que tu lui assignes. Expire automatiquement.
      </div>
      {active.length === 0 ? (
        <div style={{ padding: 16, textAlign: 'center', color: 'var(--t3)', fontFamily: 'Lexend', fontSize: 12.5, background: 'var(--sf)', borderRadius: 12, border: '1px dashed var(--bd)' }}>
          Aucun compte invité actif.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {active.map((g) => (
            <div
              key={g.id}
              data-testid={`guest-row-${g.id}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '11px 14px', borderRadius: 11,
                background: 'rgba(245,197,106,0.06)',
                border: '1.5px solid rgba(245,197,106,0.30)',
              }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'linear-gradient(180deg, #F5C56A 0%, #D99A42 100%)',
                color: '#0B1330', display: 'grid', placeItems: 'center',
                fontFamily: 'Sora', fontWeight: 800, fontSize: 12, flexShrink: 0,
              }}>{(g.name || 'I')[0].toUpperCase()}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t1)' }}>
                  {g.name || 'Invité'}
                  {g.needs_name && <span style={{ marginLeft: 6, fontSize: 10.5, color: 'var(--t3)', fontWeight: 600 }}>· prénom pas encore saisi</span>}
                </div>
                <div style={{ fontFamily: 'Lexend', fontSize: 11, color: g.status === 'expired' ? '#FF8A8A' : 'var(--t3)' }}>
                  {g.is_admin && <span data-testid={`guest-admin-badge-${g.id}`} style={{ marginRight: 6, padding: '1px 7px', borderRadius: 999, background: 'rgba(245,197,106,0.18)', color: '#F5C56A', fontWeight: 800 }}>ADMIN</span>}
                  {(GUEST_ROLE_OPTS.find((r) => r.id === g.job_role) || GUEST_ROLE_OPTS[3]).label} · {g.status === 'expired' ? 'Expiré' : fmtExpire(g.expires_at)}
                </div>
              </div>
              <GuestRowActions g={g} onUpdated={onUpdated} />
              {g.is_admin && <AdminAuditButton g={g} />}
              {g.token && <button
                data-testid={`copy-guest-link-${g.id}`}
                onClick={() => {
                  const url = `${window.location.origin}/guest/${g.token}`;
                  navigator.clipboard?.writeText(url);
                  toast.success('Lien copié');
                }}
                className="asa-btn asa-btn-ghost"
                style={{ height: 30, padding: '0 12px', fontSize: 11.5 }}
              >Copier le lien</button>}
              <button
                data-testid={`revoke-guest-${g.id}`}
                onClick={() => onRevoke(g.id)}
                className="asa-btn asa-btn-ghost"
                style={{ height: 30, padding: '0 10px', fontSize: 11.5, color: '#EF4444', borderColor: '#EF444455' }}
              >
                <Trash2 size={12} /> Révoquer
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminAuditButton({ g }) {
  const { isFounderStrict } = useAuth();
  const [open, setOpen] = useState(false);
  if (!isFounderStrict) return null;
  return (
    <>
      <button data-testid={`guest-audit-${g.id}`} onClick={() => setOpen(true)} className="asa-btn asa-btn-ghost" style={{ height: 30, padding: '0 12px', fontSize: 11.5, color: '#F5C56A', borderColor: 'rgba(245,197,106,0.45)' }}>Historique</button>
      {open && <AdminAuditDialog guest={g} onClose={() => setOpen(false)} />}
    </>
  );
}

function GuestCreatorDialog({ onClose, onCreated, created, initialRole = 'autre' }) {
  const { isFounderStrict } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState(initialRole);
  const [hours, setHours] = useState(168); // 7 jours
  const [busy, setBusy] = useState(false);
  const PRESETS = [
    { h: 1,   label: '1h' },
    { h: 24,  label: '24h' },
    { h: 168, label: '7j' },
    { h: 720, label: '30j' },
  ];
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post('/guest-accounts', { name: name.trim(), duration_hours: hours, job_role: role, is_admin: isAdmin });
      const invite = { ...r.data, invite_url: `${window.location.origin}/guest/${r.data.token}` };
      onCreated(invite);
      toast.success('Compte invité créé');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };
  return (
    <div
      data-testid="guest-creator-dialog"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 150,
        background: 'rgba(6,12,26,0.74)', backdropFilter: 'blur(12px)',
        display: 'grid', placeItems: 'center', padding: 18,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="scale-in"
        style={{
          width: '100%', maxWidth: 480,
          background: 'linear-gradient(180deg, rgba(20,29,66,0.72) 0%, rgba(14,22,44,0.98) 100%)',
          borderRadius: 22, border: '1px solid var(--bd)',
          padding: 26, boxShadow: '0 40px 80px -20px rgba(0,0,0,0.6)',
        }}
      >
        {!created ? (
          <form onSubmit={submit}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <h2 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 18, color: 'var(--t1)', margin: 0, flex: 1 }}>{role === 'posteur' ? 'Lien d\'invitation posteur' : 'Créer un compte invité'}</h2>
              <button type="button" onClick={onClose} style={{ background: 'var(--sf)', border: '1px solid var(--bd)', color: 'var(--t1)', cursor: 'pointer', width: 30, height: 30, borderRadius: 9, display: 'grid', placeItems: 'center' }}><XIcon size={14} /></button>
            </div>
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: 'var(--t3)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6 }}>Rôle</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 16 }}>
              {GUEST_ROLE_OPTS.map((r) => (
                <button key={r.id} type="button" data-testid={`guest-role-opt-${r.id}`} onClick={() => setRole(r.id)} style={{ padding: '9px 6px', borderRadius: 10, cursor: 'pointer', background: role === r.id ? 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)' : 'var(--sf)', border: `1px solid ${role === r.id ? '#5BBEFC' : 'var(--bd)'}`, color: role === r.id ? '#0B1330' : 'var(--t2)', fontFamily: 'Sora', fontWeight: 800, fontSize: 12 }}>{r.label}</button>
              ))}
            </div>
            {isFounderStrict && (
              <button type="button" data-testid="guest-admin-toggle" onClick={() => setIsAdmin((x) => !x)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px', marginBottom: 16, borderRadius: 12, cursor: 'pointer', textAlign: 'left', background: isAdmin ? 'rgba(245,197,106,0.12)' : 'var(--sf)', border: `1px solid ${isAdmin ? 'rgba(245,197,106,0.55)' : 'var(--bd)'}`, color: 'var(--t1)', fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5 }}>
                <span style={{ width: 36, height: 20, borderRadius: 999, background: isAdmin ? '#F5C56A' : 'rgba(255,255,255,0.15)', position: 'relative', flexShrink: 0, transition: 'background-color .2s' }}>
                  <span style={{ position: 'absolute', top: 2, left: isAdmin ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
                </span>
                <span style={{ flex: 1 }}>Lien admin<span style={{ display: 'block', fontWeight: 500, fontSize: 11, color: 'var(--t3)' }}>Accès à tout ASA, sauf créer des liens admin et supprimer des projets</span></span>
              </button>
            )}
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: 'var(--t3)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6 }}>Nom (optionnel)</div>
            <input
              data-testid="guest-name-input"
              name="asa-guest-alias"
              autoComplete="off"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Laisse vide → « Invité N », il mettra son prénom à la connexion"
              style={{
                width: '100%', padding: '11px 13px', borderRadius: 10,
                background: 'var(--sf)', border: '1px solid var(--bd)',
                color: 'var(--t1)', outline: 'none',
                fontFamily: 'Sora', fontWeight: 500, fontSize: 13,
                marginBottom: 16,
              }}
            />
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: 'var(--t3)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6 }}>Durée d'accès</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 10 }}>
              {PRESETS.map((p) => {
                const active = hours === p.h;
                return (
                  <button
                    key={p.h}
                    type="button"
                    data-testid={`guest-duration-${p.h}`}
                    onClick={() => setHours(p.h)}
                    style={{
                      padding: '10px 8px', borderRadius: 10,
                      background: active ? 'linear-gradient(180deg, #F5C56A 0%, #D99A42 100%)' : 'var(--sf)',
                      border: `1px solid ${active ? '#F5C56A' : 'var(--bd)'}`,
                      color: active ? '#0B1330' : 'var(--t2)',
                      fontFamily: 'Sora', fontWeight: 800, fontSize: 13, cursor: 'pointer',
                      letterSpacing: '-0.005em',
                    }}
                  >{p.label}</button>
                );
              })}
            </div>
            <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 11, color: 'var(--t4)', marginBottom: 20 }}>
              Après expiration, le lien ne fonctionne plus. Tu pourras toujours révoquer manuellement.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 10 }}>
              <button type="button" onClick={onClose} style={{ padding: '12px 16px', borderRadius: 12, background: 'transparent', border: '1px solid var(--bd)', color: 'var(--t1)', fontFamily: 'Sora', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Annuler</button>
              <button
                type="submit"
                data-testid="guest-create-submit"
                disabled={busy}
                style={{
                  padding: '12px 16px', borderRadius: 12,
                  background: 'linear-gradient(180deg, #F5C56A 0%, #D99A42 100%)',
                  border: 'none', color: '#0B1330',
                  fontFamily: 'Sora', fontWeight: 800, fontSize: 13,
                  cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
                  boxShadow: '0 8px 20px -6px rgba(245,197,106,0.55), inset 0 1px 0 rgba(255,255,255,0.28)',
                }}
              >{busy ? 'Création…' : 'Générer le lien'}</button>
            </div>
          </form>
        ) : (
          <div data-testid="guest-created-view">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(52,211,153,0.18)', border: '1px solid rgba(52,211,153,0.5)', color: '#34D399', display: 'grid', placeItems: 'center', fontSize: 18, fontWeight: 900 }}>✓</div>
              <div style={{ flex: 1 }}>
                <h2 style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 17, color: 'var(--t1)', margin: 0 }}>Compte invité créé</h2>
                <div style={{ fontFamily: 'Sora', fontSize: 11.5, color: 'var(--t3)', marginTop: 2 }}>{created.name} · {fmtExpire(created.expires_at)}</div>
              </div>
              <button type="button" onClick={onClose} style={{ background: 'var(--sf)', border: '1px solid var(--bd)', color: 'var(--t1)', cursor: 'pointer', width: 30, height: 30, borderRadius: 9, display: 'grid', placeItems: 'center' }}><XIcon size={14} /></button>
            </div>
            <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: 'var(--t3)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 6 }}>Lien à partager</div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: 8, borderRadius: 10, background: 'var(--sf)', border: '1px solid var(--bd)',
              marginBottom: 12,
            }}>
              <input
                data-testid="guest-invite-url"
                readOnly
                value={created.invite_url}
                onFocus={(e) => e.target.select()}
                style={{
                  flex: 1, minWidth: 0, border: 'none', background: 'transparent',
                  color: 'var(--t1)', fontFamily: 'JetBrains Mono, monospace', fontSize: 12,
                  outline: 'none', padding: '6px 8px',
                }}
              />
              <button
                data-testid="guest-copy-url-btn"
                type="button"
                onClick={() => { navigator.clipboard?.writeText(created.invite_url); toast.success('Lien copié'); }}
                style={{
                  padding: '7px 14px', borderRadius: 8,
                  background: 'linear-gradient(180deg, #F5C56A 0%, #D99A42 100%)',
                  border: 'none', color: '#0B1330',
                  fontFamily: 'Sora', fontWeight: 800, fontSize: 11.5, cursor: 'pointer',
                }}
              >Copier</button>
            </div>
            <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 11.5, color: 'var(--t4)', marginBottom: 16, lineHeight: 1.5 }}>
              Envoie-le à {created.name}. Il atterrit directement sur l'onglet Tâches. Tu peux maintenant lui assigner des tâches depuis "Nouvelle tâche".
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="guest-created-close"
              style={{
                width: '100%', padding: '12px 16px', borderRadius: 12,
                background: 'var(--sf)', border: '1px solid var(--bd)',
                color: 'var(--t1)', fontFamily: 'Sora', fontWeight: 700, fontSize: 13, cursor: 'pointer',
              }}
            >Terminé</button>
          </div>
        )}
      </div>
    </div>
  );
}

